import { OpenPositionRow, OpenPositionTableProps } from '@/components/trader-modules/Blotters/OpenPositionTable';
import { GroupBy } from '@/components/trader-modules/Blotters/PositionGroupSwitch';
import { convertedQtyFormatterConfig, defaultColumn, qtyFormatterConfig } from '@/config/config';
import { formatInstrumentType } from '@/helpers/blotterHelper';
import { openPositionColumns } from '@/helpers/openPositionHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { OpenPosition } from '@/state/reducers/openPositionSlice';
import { store } from '@/state/store';
import { useDebouncedValue } from '@/utils/hooks/useDebouncedValue';
import useInterval from '@/utils/hooks/useInterval';
import { translatePair } from '@/utils/symbolMapping';
import {
    ColumnDef,
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { DateFormatter, useDateFormatter, useNumberFormatter } from 'react-aria';
import { useDidUpdate } from './useDidUpdate';
import { getUniversalInstrument } from './useInstrument';
import useLanguage from './useLanguage';

const basicGrouping = (item, groupedMap) => {
    const {
        instrument,
        tableInstrument,
        type,
        market,
        netQtyPosition,
        openPrice,
        currentPrice,
        unrealisedPnl,
        realisedPnl,
        settlementDate,
        accountCode
    } = item;
    if (groupedMap.has(tableInstrument)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const group = groupedMap.get(tableInstrument)!;

        const convertedPosition = BigNumber(netQtyPosition).multipliedBy(openPrice).toNumber();
        const sumOfNetPosition = BigNumber(group.netQtyPosition).plus(netQtyPosition).toNumber();

        group.openPrice =
            sumOfNetPosition !== 0
                ? BigNumber(convertedPosition)
                      .plus(BigNumber(group.openPrice).multipliedBy(group.netQtyPosition))
                      .dividedBy(sumOfNetPosition)
                      .toNumber()
                : '-';
        group.currentPrice = currentPrice;
        group.netQtyPosition = BigNumber(group.netQtyPosition).plus(netQtyPosition).toNumber();

        group.unrealisedPnl = {
            value: BigNumber(group.unrealisedPnl?.value || 0)
                .plus(unrealisedPnl.value || 0)
                .toNumber(),
            currency: unrealisedPnl.currency
        };
        group.realisedPnl = {
            value: BigNumber(group.realisedPnl?.value || 0)
                .plus(realisedPnl.value || 0)
                .toNumber(),
            currency: realisedPnl.currency
        };

        group.subRows.push({
            instrument,
            tableInstrument,
            type,
            netQtyPosition,
            openPrice,
            currentPrice,
            unrealisedPnl,
            realisedPnl,
            settlementDate,
            accountCode
        });
    } else {
        groupedMap.set(tableInstrument, {
            instrument,
            tableInstrument,
            market,
            type,
            netQtyPosition,
            openPrice,
            currentPrice,
            unrealisedPnl,
            realisedPnl,
            settlementDate,
            accountCode,
            subRows: [
                {
                    instrument,
                    tableInstrument,
                    type,
                    netQtyPosition,
                    openPrice,
                    currentPrice,
                    unrealisedPnl,
                    realisedPnl,
                    settlementDate,
                    accountCode
                }
            ]
        });
    }
};

const groupRowsByInstrument = (rows: OpenPositionRow[], groupBy) => {
    const groupedMap = new Map<string, any>();
    const productGroupedMap = new Map<string, any>();

    if (groupBy === 'None') {
        rows.forEach((item) => basicGrouping(item, groupedMap));
        const returnValues = Array.from(groupedMap.values());

        // Remove instrument subRows if length is 1
        returnValues.forEach((item) => {
            if (item.subRows.length === 1) {
                item.subRows = [];
            }
        });

        return returnValues;
    } else if (groupBy === 'Products') {
        rows.forEach((item) => {
            const {
                instrument,
                tableInstrument,
                type,
                market,
                netQtyPosition,
                openPrice,
                currentPrice,
                unrealisedPnl,
                realisedPnl,
                settlementDate,
                accountCode
            } = item;

            if (productGroupedMap.has(type)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const productGroup = productGroupedMap.get(type)!;
                productGroup.unrealisedPnl = {
                    value: BigNumber(productGroup.unrealisedPnl?.value || 0)
                        .plus(unrealisedPnl.value || 0)
                        .toNumber(),
                    currency: unrealisedPnl.currency
                };
                productGroup.realisedPnl = {
                    value: BigNumber(productGroup.realisedPnl?.value || 0)
                        .plus(realisedPnl.value || 0)
                        .toNumber(),
                    currency: realisedPnl.currency
                };
                basicGrouping(item, productGroup.subRows);
            } else {
                productGroupedMap.set(type, {
                    instrument,
                    tableInstrument,
                    market,
                    type,
                    netQtyPosition,
                    openPrice,
                    currentPrice,
                    unrealisedPnl,
                    realisedPnl,
                    settlementDate,
                    accountCode,
                    subRows: new Map<string, any>().set(tableInstrument, {
                        instrument,
                        tableInstrument,
                        market,
                        type,
                        netQtyPosition,
                        openPrice,
                        currentPrice,
                        unrealisedPnl,
                        realisedPnl,
                        settlementDate,
                        accountCode,
                        subRows: [
                            {
                                instrument,
                                tableInstrument,
                                type,
                                netQtyPosition,
                                openPrice,
                                currentPrice,
                                unrealisedPnl,
                                realisedPnl,
                                settlementDate,
                                accountCode
                            }
                        ]
                    })
                });
            }
        });
        const returnValues = Array.from(productGroupedMap.keys()).map((key) => {
            const productGroup = productGroupedMap.get(key);
            return {
                instrument: key,
                tableInstrument: key,
                market: key,
                type: key,
                netQtyPosition: key,
                openPrice: key,
                currentPrice: key,
                unrealisedPnl: productGroup.unrealisedPnl,
                realisedPnl: productGroup.realisedPnl,
                settlementDate: key,
                accountCode: key,
                subRows: Array.from(productGroup.subRows.values())
            };
        });

        // Remove instrument subRows if length is 1
        returnValues.forEach((group) => {
            group.subRows.forEach((item: any) => {
                if (item.subRows.length === 1) {
                    item.subRows = [];
                }
            });
        });

        return returnValues;
    }
    return [];
};

interface UseOpenPositionsTableProps extends OpenPositionTableProps {
    groupBy: GroupBy;
    positionFilter: boolean;
    setInstrumentInfoContext: React.Dispatch<React.SetStateAction<any>>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const useOpenPositionsTable = (props: UseOpenPositionsTableProps) => {
    const { groupBy, positionFilter, setCSVData, onOrderModal, setInstrumentInfoContext, handleOpenContextMenu } =
        props;

    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const formatQty = useNumberFormatter(qtyFormatterConfig);
    const formatConvertedQty = useNumberFormatter(convertedQtyFormatterConfig);

    const lang = useLanguage();

    const [debouncedOpenPositions, setDebouncedOpenPositions] = useState<OpenPosition[]>([]);
    const [openPositionsHydated, setOpenPositionsHydated] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [globalFilter, setGlobalFilter] = useState<string>('');

    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const currentAccountProperties = useMemo(
        () => credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [],
        [credentials, currentAccount]
    );

    const accountType = useMemo(
        () => currentAccountProperties.find((property) => property.key === 'TYPE')?.value as 'NOP' | 'MARGIN',
        [currentAccountProperties]
    );

    const columns: ColumnDef<OpenPositionRow>[] = useMemo(() => {
        return openPositionColumns({
            lang,
            onOrderModal,
            groupBy,
            formatQty,
            formatConvertedQty,
            setInstrumentInfoContext,
            handleOpenContextMenu
        });
    }, [lang, onOrderModal, groupBy, formatQty, formatConvertedQty, setInstrumentInfoContext, handleOpenContextMenu]);

    const formattedOpenPositions: OpenPositionRow[] = useMemo(() => {
        const returnValues = debouncedOpenPositions
            .filter(({ positionUpdate }) => positionUpdate?.accountCode === currentAccount)
            .filter(({ positionUpdate }) => positionFilter || positionUpdate?.netQtyPosition !== 0)
            .map((openPosition) => {
                const { openPrice, positionUpdate, unrealisedPnl } = openPosition;
                const bidAsk = store.getState().marketPair.bidAsk[positionUpdate?.securityCode || ''];
                const currentPrice = bidAsk ? BigNumber(bidAsk.ask).plus(bidAsk.bid).div(2).toNumber() : '-';
                let tableInstrument = translatePair(positionUpdate?.securityCode || '');
                const instrumentConfig = getUniversalInstrument(positionUpdate?.securityCode || '');

                if (instrumentConfig?.type === 'Future') tableInstrument = instrumentConfig.gcexSymbol;

                const market = {
                    celer: positionUpdate?.securityCode || '',
                    netdania: positionUpdate?.securityCode || '',
                    show: tableInstrument
                } as PairMap;

                return {
                    ...openPosition.positionUpdate,
                    type: formatInstrumentType(instrumentConfig, accountType) || '',
                    instrument: positionUpdate?.securityCode || '',
                    market,
                    tableInstrument,
                    openPrice,
                    unrealisedPnl: {
                        value: unrealisedPnl,
                        currency: positionUpdate?.realisedPnLCurrency || ''
                    },
                    realisedPnl: {
                        value: positionUpdate?.realisedPnl,
                        currency: positionUpdate?.realisedPnLCurrency || ''
                    },
                    currentPrice,
                    settlementDate: positionUpdate?.settlementDate || '-'
                };
            }) as OpenPositionRow[];

        returnValues.sort((a, b) => {
            if (a.tableInstrument < b.tableInstrument) return -1;
            if (a.tableInstrument > b.tableInstrument) return 1;
            return 0;
        });

        return groupRowsByInstrument(returnValues, groupBy);
    }, [debouncedOpenPositions, currentAccount, accountType, positionFilter, groupBy]);

    const tableInstance = useReactTable({
        data: formattedOpenPositions,
        columns,
        state: { expanded: expandedRows, globalFilter },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnIds, filterValue) => fuzzyGlobalFilter(row, columnIds, filterValue, dateFormatter),
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 5000);

    useEffect(() => {
        setCSVData(formattedOpenPositions);
    }, [formattedOpenPositions]);

    useDidUpdate(() => {
        if (groupBy === 'None') {
            setExpandedRows({});
        } else if (groupBy === 'Products') {
            const newExpandedRows = {};
            formattedOpenPositions.forEach((row, i) => {
                newExpandedRows[i] = true;
            });
            setExpandedRows(newExpandedRows);
        }
    }, [groupBy, openPositionsHydated, positionFilter, currentAccount]);

    useInterval(
        () => {
            const openPositions = store.getState().openPosition.openPositions;
            setDebouncedOpenPositions(openPositions);
            if (debouncedOpenPositions.length === 0 && openPositions.length !== 0) {
                // Run this every time when open positions are hydrated in the table.
                // Doesn't matter if this state is true or false, we just need it to trigger the useEffect above
                setOpenPositionsHydated(!openPositionsHydated);
            }
        },
        5000,
        true
    );

    return { debouncedTableInstance, debouncedOpenPositions, expandedRows, globalFilter };
};

export default useOpenPositionsTable;

const normalize = (value: number | string) =>
    String(value ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

const getNormalizedRowValues = (row: any, dateFormatter: DateFormatter): string => {
    return Object.values(row.original)
        .flatMap((value) => {
            if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) {
                return normalize(dateFormatter.format(new Date(value)));
            }
            return normalize(String(value ?? ''));
        })
        .join('');
};

export function fuzzyGlobalFilter(row: any, columnIds: any, filterValue: string, dateFormatter: DateFormatter) {
    if (!filterValue?.trim()) return true;

    const normalizedFilter = normalize(filterValue);

    const rowValues = getNormalizedRowValues(row, dateFormatter);
    return rowValues.includes(normalizedFilter) || recursivelySearchOnSubrows(row, normalizedFilter, dateFormatter);
}

export const recursivelySearchOnSubrows = (
    row: any,
    normalizedFilter: string,
    dateFormatter: DateFormatter
): boolean => {
    if (row.subRows?.length) {
        return row.subRows.some((subRow) => recursivelySearchOnSubrows(subRow, normalizedFilter, dateFormatter));
    }

    const rowValues = getNormalizedRowValues(row, dateFormatter);
    return rowValues.includes(normalizedFilter);
};
